import React from "react";
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Form } from 'react-bootstrap';
import FileInput from "../../Components/UploadFiles/FileInput";
import FilesInput from "../../Components/UploadFiles/FilesInput";
import styled from "styled-components";
import { TextEditor } from "../../Components/TextEditor";
import { Company } from "../../types";

const StyledDiv = styled.div`
    @media screen and (min-width: 768px) {
        margin-right: 7%;
}`

type EditCommonInformationProps = {
    company: Company
    children?: React.ReactNode
    companyName: string
    website: string
    description: string
    onCompanyNameChange: (value: string) => void
    onWebsiteChange: (value: string) => void
    onDescriptionChange: (value: string) => void
    onDataChange: (value: boolean) => void
}

function EditCommonInformation({company, children, companyName, website, description, onCompanyNameChange, onWebsiteChange, onDescriptionChange, onDataChange} : EditCommonInformationProps, ref: any) {
    const logoRef = useRef<{ save: () => Promise<void> } | null>(null);
    const imagesRef = useRef<{ save: () => Promise<void> } | null>(null);

    const [isLogoChanged, setIsLogoChanged] = useState(false);
    const [isImagesChanged, setIsImagesChanged] = useState(false);

    const [companyNameDirty, setCompanyNameDirty] = useState(false);
    const [companyNameError, setCompanyNameError] = useState('Укажите название компании');

    const regWebsite = /\S+\.\S+/;
    
    const [websiteDirty, setWebsiteDirty] = useState(false);
    const [websiteError, setWebsiteError] = useState('Укажите сайт компании');

    const [logoId, setLogoId] = useState('');
    const [imagesIds, setImagesIds] = useState<string[] | undefined>([])

    const blurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
        switch (e.target.name) {
            case 'companyName':
                setCompanyNameDirty(true)
                break;
            case 'website':
                setWebsiteDirty(true)
                break;
        }
    }

    const companyNameHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        onCompanyNameChange(e.target.value);
        onDataChange(true);
        if (e.target.value.length === 0) {
            setCompanyNameError('Название компании не может быть пустым')
        } else if (e.target.value.length < 2) {
            setCompanyNameError('Название компании не может состоять из одного символа')
        } else {
            setCompanyNameError('')
        }
    }

    const websiteHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        onWebsiteChange(e.target.value);
        onDataChange(true);
        if (!regWebsite.test(String(e.target.value).toLowerCase())) {
            setWebsiteError('Некорректный адрес сайта')
        } else {
            setWebsiteError('')
        }
    }

    const handleDescription = (e: string) => {
        onDescriptionChange(e);
        onDataChange(true);
    }

    const checkErrors = () => {
        setCompanyNameDirty(true);
        setWebsiteDirty(true);
        return (companyNameError.length !== 0 || websiteError.length !== 0);
    }

    const saveImages = async () => {
        const updatePromises = [];

        if (isLogoChanged && logoRef.current) {
            logoRef.current.save();
        }

        if (isImagesChanged && imagesRef.current) {
            const imagesPromise = imagesRef.current.save();
            updatePromises.push(imagesPromise);
        }
        return Promise.all(updatePromises);
    }

    useEffect(() => {
        if (companyName) {
            setCompanyNameError('');
        }
        if (website) {
            setWebsiteError('');
        }
        setLogoId(company?.logoId)
        setImagesIds(company?.imagesIds)
    }, [company]);

    useImperativeHandle(ref, () => ({ checkErrors, saveImages }));

    return (
        <>
            <h2 style={{ marginBottom: '20px', marginTop: '24px' }}> Общая информация </h2>
            <div className="d-table" style={{marginBottom: '122px'}}>
                <div className="d-table-row d-md-table-cell">
                    <StyledDiv>
                        <div className="form-group">
                            <Form.Label> Название компании </Form.Label>
                            <Form.Control type="text" name='companyName'
                                value={companyName} 
                                onBlur={blurHandler} 
                                onChange={companyNameHandler}
                                isInvalid={!!(companyNameError && companyNameDirty)} />
                            {(companyNameDirty && companyNameError) && <p className="error-alert">{companyNameError}</p>}
                        </div>
                        <div className="form-group" style={{ marginBottom: '32px' }}>
                            <Form.Label> Сайт </Form.Label>
                            <Form.Control type="text" name='website'
                                value={website} 
                                onBlur={blurHandler} 
                                onChange={websiteHandler}
                                isInvalid={!!(websiteError && websiteDirty)} />
                            {(websiteDirty && websiteError) && <p className="error-alert">{websiteError}</p>}
                        </div>
                        <div className="form-group">
                            <Form.Label> 
                                Подробное описание <span style={{whiteSpace: 'nowrap', color:'#76838F'}}>(отображается на странице компании)</span>  
                            </Form.Label>
                            <TextEditor 
                                theme="snow"
                                value={description}
                                onChange={handleDescription}
                            />
                        </div>
                    </StyledDiv>
                </div>
                <div className="d-table-row d-md-table-cell" style={{width: '50%'}}>
                    <div>
                        <div className="form-group">
                            <Form.Label> Лого компании </Form.Label>
                            <FileInput ref={logoRef} fileInit={logoId} onChange={setIsLogoChanged} imageName="Images"/>
                        </div>
                        <div className="form-group">
                            <Form.Label> Фото компании </Form.Label>
                            <FilesInput ref={imagesRef} filesInit={imagesIds} onChange={setIsImagesChanged}/>
                        </div>
                    </div>
                </div>
            </div>
            <footer>
                <div style={{marginLeft:'6%', paddingTop: '16px'}}>
                    {children}
                </div>
            </footer>
        </>
    )
}

export default forwardRef(EditCommonInformation);